import axiosInstance from './axiosCreator';
import {
  CohortPerformanceContext,
  CohortPerformanceOverview,
  CohortPerformanceScope,
  CohortPerformanceSelfAssessments,
  CohortPerformanceStudents,
  CohortPerformanceTopics,
  CohortPerformanceWindow,
  CohortTopicStudents,
  CohortTopicView,
  InstitutionAdminCohort,
  InstitutionAdminMemberSort,
  InstitutionAdminMemberStatusFilter,
  InstitutionAdminMembersResponse,
  InstitutionAdminPerformanceContext,
  InstitutionAdminScope,
  InstitutionAdminSortDirection,
  InstitutionAdminStaffResponse,
  InstitutionAdminStudentLectureProgress,
} from '../types/InstitutionAdmin';
import type { Block, BlockTimeSetting } from '../types/Study';
import type { TopicTreeView } from './topics';
import type {
  TopicTreeStatsResponse,
  TopicTreeStatsWithReadiness,
} from './stats';
import type {
  FlashcardCountRequest,
  WorkloadFlashcardCountRequest,
} from './flashcards';
import type {
  SelfAssessmentBlock,
  SelfAssessmentExportFormat,
  SelfAssessmentResults,
  SelfAssessmentReviewBlockRequest,
} from './selfAssessments';
import { parseContentDispositionFilename } from '../utils/downloadFile';

type ApiResponse<T> = {
  success: boolean;
  data: T;
};

const unwrap = <T>(response: { data: ApiResponse<T> }): T => response.data.data;

export const getInstitutionAdminScope =
  async (): Promise<InstitutionAdminScope> => {
    const response = await axiosInstance.get<
      ApiResponse<InstitutionAdminScope>
    >('/institution-admin/scope');

    return unwrap(response);
  };

export const getInstitutionAdminCohorts = async (): Promise<{
  organization: { id: string; name: string };
  cohorts: InstitutionAdminCohort[];
}> => {
  const response = await axiosInstance.get<
    ApiResponse<{
      organization: { id: string; name: string };
      cohorts: InstitutionAdminCohort[];
    }>
  >('/institution-admin/cohorts');

  return unwrap(response);
};

export const getInstitutionAdminCohortMembers = async ({
  cohortId,
  search,
  status,
  sort,
  direction,
  page,
}: {
  cohortId: string;
  search?: string;
  status?: InstitutionAdminMemberStatusFilter;
  sort?: InstitutionAdminMemberSort;
  direction?: InstitutionAdminSortDirection;
  page?: number;
}): Promise<InstitutionAdminMembersResponse> => {
  const params = new URLSearchParams();
  if (search) params.set('search', search);
  if (status) params.set('status', status);
  if (sort) params.set('sort', sort);
  if (direction) params.set('direction', direction);
  if (page) params.set('page', String(page));

  const query = params.toString();

  const response = await axiosInstance.get<
    ApiResponse<InstitutionAdminMembersResponse>
  >(
    `/institution-admin/cohorts/${cohortId}/members${query ? `?${query}` : ''}`
  );

  return unwrap(response);
};

export const getInstitutionAdminCohortStaff = async (
  cohortId: string
): Promise<InstitutionAdminStaffResponse> => {
  const response = await axiosInstance.get<
    ApiResponse<InstitutionAdminStaffResponse>
  >(`/institution-admin/cohorts/${cohortId}/staff`);

  return unwrap(response);
};

export type InstitutionAdminCohortTopicPerformance = {
  view: TopicTreeView;
  allowedViews: TopicTreeView[];
  studentCount: number;
  topics: {
    name: string;
    correct: number;
    total: number;
    percent: number;
    // Present only with `readinessEnabled` (ORA-1105); null when limited.
    readiness?: number | null;
    readinessStudents?: number;
    readinessLimitedData?: boolean;
  }[];
  readinessEnabled?: true;
};

// Cohort-level aggregate GLOBAL topic performance for the faculty radar. Pooled
// across all eligible cohort students (not scoped to any exam); `view` switches
// the global topic-tree lens (Systems / Subjects / Specialties).
export const getInstitutionAdminCohortTopicPerformance = async ({
  cohortId,
  view,
}: {
  cohortId: string;
  view?: TopicTreeView;
}): Promise<InstitutionAdminCohortTopicPerformance> => {
  const response = await axiosInstance.get<
    ApiResponse<InstitutionAdminCohortTopicPerformance>
  >(
    `/institution-admin/cohorts/${cohortId}/topic-performance${buildQuery({
      view,
    })}`
  );

  return unwrap(response);
};

export type InstitutionAdminCohortTopVideo = {
  id: string;
  title: string | null;
  thumbnailUrl: string | null;
};

// Up to five videos the cohort engages with most, ranked server-side by distinct
// completers (then watchers). No filters — a flat list across the cohort.
export const getInstitutionAdminCohortTopVideos = async ({
  cohortId,
}: {
  cohortId: string;
}): Promise<{ videos: InstitutionAdminCohortTopVideo[] }> => {
  const response = await axiosInstance.get<
    ApiResponse<{ videos: InstitutionAdminCohortTopVideo[] }>
  >(`/institution-admin/cohorts/${cohortId}/top-videos`);

  return unwrap(response);
};

export type InstitutionAdminCohortMostActiveStudent = {
  rosterEntryId: string;
  displayName: string;
  questionsAnswered: number;
};

// Up to five students ranked server-side by all-time QBank volume (first-attempt
// questions answered). Only students faculty can view a profile for are returned,
// so every row links to the student detail page.
export const getInstitutionAdminCohortMostActiveStudents = async ({
  cohortId,
}: {
  cohortId: string;
}): Promise<{ students: InstitutionAdminCohortMostActiveStudent[] }> => {
  const response = await axiosInstance.get<
    ApiResponse<{ students: InstitutionAdminCohortMostActiveStudent[] }>
  >(`/institution-admin/cohorts/${cohortId}/most-active-students`);

  return unwrap(response);
};

// Faculty-writable counterpart to the (student-locked) time-accommodation
// setting. Returns the persisted value so callers can update their view in place.
export const updateInstitutionAdminStudentAccommodation = async ({
  cohortId,
  rosterEntryId,
  preferredBlockTimeSetting,
}: {
  cohortId: string;
  rosterEntryId: string;
  preferredBlockTimeSetting: BlockTimeSetting;
}): Promise<{
  rosterEntryId: string;
  preferredBlockTimeSetting: BlockTimeSetting;
}> => {
  const response = await axiosInstance.patch<
    ApiResponse<{
      rosterEntryId: string;
      preferredBlockTimeSetting: BlockTimeSetting;
    }>
  >(
    `/institution-admin/cohorts/${cohortId}/members/${rosterEntryId}/accommodations`,
    { preferredBlockTimeSetting }
  );

  return unwrap(response);
};

// ---------------------------------------------------------------------------
// Faculty-scoped student performance. These back the reused dashboard widgets
// on the faculty student-profile page; each mirrors its /api/v5/stats
// counterpart but is bound to a roster entry the faculty admin may view.
// ---------------------------------------------------------------------------

const performanceBase = (cohortId: string, rosterEntryId: string) =>
  `/institution-admin/cohorts/${cohortId}/members/${rosterEntryId}/performance`;

const buildQuery = (
  params: Record<string, string | number | undefined>
): string => {
  const search = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') search.append(key, String(value));
  });

  const query = search.toString();
  return query ? `?${query}` : '';
};

export const getInstitutionAdminStudentPerformanceContext = async (
  cohortId: string,
  rosterEntryId: string
): Promise<InstitutionAdminPerformanceContext> => {
  const response = await axiosInstance.get<
    ApiResponse<InstitutionAdminPerformanceContext>
  >(`${performanceBase(cohortId, rosterEntryId)}/context`);

  return unwrap(response);
};

export const getInstitutionAdminStudentVanity = async (
  cohortId: string,
  rosterEntryId: string,
  examId?: string
) => {
  const response = await axiosInstance.get<ApiResponse<unknown>>(
    `${performanceBase(cohortId, rosterEntryId)}/vanity${buildQuery({ examId })}`
  );

  return unwrap(response);
};

export const getInstitutionAdminStudentHistory = async (
  cohortId: string,
  rosterEntryId: string,
  examId?: string,
  days?: number
) => {
  const response = await axiosInstance.get<ApiResponse<unknown>>(
    `${performanceBase(cohortId, rosterEntryId)}/history${buildQuery({
      examId,
      days,
    })}`
  );

  return unwrap(response);
};

export const getInstitutionAdminStudentQbankPerformance = async (
  cohortId: string,
  rosterEntryId: string,
  examId?: string,
  timeframe?: number
) => {
  const response = await axiosInstance.get<ApiResponse<unknown>>(
    `${performanceBase(cohortId, rosterEntryId)}/qbank-performance${buildQuery({
      examId,
      timeframe,
    })}`
  );

  return unwrap(response);
};

export const getInstitutionAdminStudentFlashcardProgress = async (
  cohortId: string,
  rosterEntryId: string,
  examId?: string
) => {
  const response = await axiosInstance.get<ApiResponse<unknown>>(
    `${performanceBase(cohortId, rosterEntryId)}/flashcard-progress${buildQuery(
      {
        examId,
      }
    )}`
  );

  return unwrap(response);
};

export const getInstitutionAdminStudentProgress = async (
  cohortId: string,
  rosterEntryId: string,
  examId: string
) => {
  const response = await axiosInstance.get<ApiResponse<unknown>>(
    `${performanceBase(cohortId, rosterEntryId)}/progress${buildQuery({
      examId,
    })}`
  );

  return unwrap(response);
};

export const getInstitutionAdminStudentTopicTree = async (
  cohortId: string,
  rosterEntryId: string,
  examId?: string,
  view?: TopicTreeView
) => {
  const response = await axiosInstance.get<ApiResponse<unknown>>(
    `${performanceBase(cohortId, rosterEntryId)}/topic-tree${buildQuery({
      examId,
      view,
    })}`
  );

  return unwrap(response);
};

// The same route as getInstitutionAdminStudentTopicTree, also reading the
// envelope's `readinessEnabled` (ORA-1105), which sits beside `data` as on
// the student's own /api/v5/stats/topic-tree.
export const getInstitutionAdminStudentTopicTreeWithReadiness = async (
  cohortId: string,
  rosterEntryId: string,
  examId?: string,
  view?: TopicTreeView
): Promise<TopicTreeStatsWithReadiness> => {
  const response = await axiosInstance.get<TopicTreeStatsResponse>(
    `${performanceBase(cohortId, rosterEntryId)}/topic-tree${buildQuery({
      examId,
      view,
    })}`
  );

  return {
    tree: response.data.data,
    readinessEnabled: response.data.readinessEnabled === true,
    // Session Summary ranking is student-only; the faculty route never sends it.
    readinessRankingEnabled: false,
  };
};

export const getInstitutionAdminStudentTopicPerformance = async (
  cohortId: string,
  rosterEntryId: string,
  examId?: string,
  view?: TopicTreeView
) => {
  const response = await axiosInstance.get<ApiResponse<unknown>>(
    `${performanceBase(cohortId, rosterEntryId)}/topic-performance${buildQuery({
      examId,
      view,
    })}`
  );

  return unwrap(response);
};

// Read-only per-lecture ("assignment") progress for the student's official
// institutional custom exam. The exam is resolved server-side, so there is no
// examId param. Returns `exam: null` when the student has no such exam.
export const getInstitutionAdminStudentLectureProgress = async (
  cohortId: string,
  rosterEntryId: string
): Promise<InstitutionAdminStudentLectureProgress> => {
  const response = await axiosInstance.get<
    ApiResponse<InstitutionAdminStudentLectureProgress>
  >(`${performanceBase(cohortId, rosterEntryId)}/lecture-progress`);

  return unwrap(response);
};

// Card-state endpoints return the same `{ success, data }` envelope the v5
// flashcard-count service returns (CardStates resolves the nested data itself).
export const getInstitutionAdminStudentCardCounts = async (
  cohortId: string,
  rosterEntryId: string,
  payload: FlashcardCountRequest
) => {
  const response = await axiosInstance.post(
    `${performanceBase(cohortId, rosterEntryId)}/card-counts`,
    payload
  );

  return response.data;
};

export const getInstitutionAdminStudentWorkloadCardCounts = async (
  cohortId: string,
  rosterEntryId: string,
  payload: WorkloadFlashcardCountRequest
) => {
  const response = await axiosInstance.post(
    `${performanceBase(cohortId, rosterEntryId)}/card-counts/workload`,
    payload
  );

  return response.data;
};

// ---------------------------------------------------------------------------
// Faculty-scoped, read-only self-assessments (mirrors the student-facing v5
// self-assessment surface, but resolved to a roster member the faculty admin
// is authorized to view).
// ---------------------------------------------------------------------------

const memberBase = (cohortId: string, rosterEntryId: string) =>
  `/institution-admin/cohorts/${cohortId}/members/${rosterEntryId}`;

const selfAssessmentBase = (cohortId: string, rosterEntryId: string) =>
  `${memberBase(cohortId, rosterEntryId)}/self-assessments`;

export type InstitutionAdminStudentSelfAssessment = {
  id: string;
  slug: string;
  exam: string;
  // null for a course assessment (ORA-1126).
  examSlug: string | null;
  status: 'in_progress' | 'completed';
  blockCount: number;
  blocksComplete: number;
  scorePct: number | null;
  startedAt: string;
  completedAt: string | null;
};

export const getInstitutionAdminStudentSelfAssessments = async (
  cohortId: string,
  rosterEntryId: string
): Promise<InstitutionAdminStudentSelfAssessment[]> => {
  const response = await axiosInstance.get<
    ApiResponse<InstitutionAdminStudentSelfAssessment[]>
  >(selfAssessmentBase(cohortId, rosterEntryId));

  return unwrap(response);
};

export const getInstitutionAdminStudentSelfAssessmentResults = async (
  cohortId: string,
  rosterEntryId: string,
  assessmentSlug: string
): Promise<SelfAssessmentResults> => {
  const response = await axiosInstance.get<ApiResponse<SelfAssessmentResults>>(
    `${selfAssessmentBase(cohortId, rosterEntryId)}/${assessmentSlug}/results`
  );

  return unwrap(response);
};

export const downloadInstitutionAdminStudentSelfAssessmentExport = async (
  cohortId: string,
  rosterEntryId: string,
  assessmentSlug: string,
  format: SelfAssessmentExportFormat
): Promise<{ blob: Blob; filename: string | null }> => {
  const response = await axiosInstance.get<Blob>(
    `${selfAssessmentBase(cohortId, rosterEntryId)}/${assessmentSlug}/export`,
    { params: { format }, responseType: 'blob' }
  );

  return {
    blob: response.data,
    filename: parseContentDispositionFilename(
      response.headers['content-disposition'] as string | undefined
    ),
  };
};

export const exportInstitutionAdminCohort = async (
  cohortId: string
): Promise<{ blob: Blob; filename: string | null }> => {
  const response = await axiosInstance.get<Blob>(
    `/institution-admin/cohorts/${cohortId}/export`,
    { responseType: 'blob' }
  );

  return {
    blob: response.data,
    filename: parseContentDispositionFilename(
      response.headers['content-disposition'] as string | undefined
    ),
  };
};

export const createInstitutionAdminStudentSelfAssessmentReviewBlock = async (
  cohortId: string,
  rosterEntryId: string,
  assessmentSlug: string,
  payload: SelfAssessmentReviewBlockRequest
): Promise<{ block: SelfAssessmentBlock }> => {
  const response = await axiosInstance.post<
    ApiResponse<{ block: SelfAssessmentBlock }>
  >(
    `${selfAssessmentBase(cohortId, rosterEntryId)}/${assessmentSlug}/review-blocks`,
    payload
  );

  return unwrap(response);
};

// Returns the full `{ success, data }` envelope, mirroring `getQbankBlockById`
// so the standalone block-review loader can normalize it the same way.
export const getInstitutionAdminStudentQbankBlock = async (
  cohortId: string,
  rosterEntryId: string,
  blockId: string
) => {
  const response = await axiosInstance.get(
    `${selfAssessmentBase(cohortId, rosterEntryId)}/blocks/${blockId}`
  );

  return response.data;
};

type QbankBlocksMeta = {
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
};

// Faculty-scoped, read-only list of a student's "previous" qbank blocks. Mirrors
// the student-facing `getQbankBlocks` exactly (started, non-self-assessment,
// non-"view all", all exams), resolved to a roster member the admin may view.
export const getInstitutionAdminStudentQbankBlocks = async (
  cohortId: string,
  rosterEntryId: string,
  page = 1,
  limit = 10
): Promise<{ blocks: Block[]; meta: QbankBlocksMeta }> => {
  const response = await axiosInstance.get<{
    success: boolean;
    data: { blocks: Block[] };
    meta: QbankBlocksMeta;
  }>(
    `${memberBase(cohortId, rosterEntryId)}/qbank/blocks${buildQuery({ page, limit })}`
  );

  return { blocks: response.data.data.blocks, meta: response.data.meta };
};

// ---------------------------------------------------------------------------
// Cohort Performance tab (ORA-851). Read-only cohort aggregates; default
// filter values (`scope=all`, `window=all`) are omitted from the query.
// ---------------------------------------------------------------------------

const cohortPerformanceBase = (cohortId: string) =>
  `/institution-admin/cohorts/${cohortId}/performance`;

const nonDefault = <T extends string>(value: T | undefined, fallback: T) =>
  value === undefined || value === fallback ? undefined : value;

export const getCohortPerformanceContext = async (
  cohortId: string
): Promise<CohortPerformanceContext> => {
  const response = await axiosInstance.get<
    ApiResponse<CohortPerformanceContext>
  >(`${cohortPerformanceBase(cohortId)}/context`);

  return unwrap(response);
};

export const getCohortPerformanceOverview = async ({
  cohortId,
  scope,
  window,
}: {
  cohortId: string;
  scope?: CohortPerformanceScope;
  window?: CohortPerformanceWindow;
}): Promise<CohortPerformanceOverview> => {
  const response = await axiosInstance.get<
    ApiResponse<CohortPerformanceOverview>
  >(
    `${cohortPerformanceBase(cohortId)}/overview${buildQuery({
      scope: nonDefault(scope, 'all'),
      window: nonDefault(window, 'all'),
    })}`
  );

  return unwrap(response);
};

// Without `parentId`: level-1 topics, each with its level-2 `children`. With
// `parentId`: that topic's direct children only (lazy tree expansion).
export const getCohortPerformanceTopics = async ({
  cohortId,
  view,
  scope,
  parentId,
}: {
  cohortId: string;
  view?: CohortTopicView;
  scope?: CohortPerformanceScope;
  parentId?: string;
}): Promise<CohortPerformanceTopics> => {
  const response = await axiosInstance.get<
    ApiResponse<CohortPerformanceTopics>
  >(
    `${cohortPerformanceBase(cohortId)}/topics${buildQuery({
      view,
      scope: nonDefault(scope, 'all'),
      parentId,
    })}`
  );

  return unwrap(response);
};

export const getCohortPerformanceTopicStudents = async ({
  cohortId,
  topicId,
  scope,
}: {
  cohortId: string;
  topicId: string;
  scope?: CohortPerformanceScope;
}): Promise<CohortTopicStudents> => {
  const response = await axiosInstance.get<ApiResponse<CohortTopicStudents>>(
    `${cohortPerformanceBase(cohortId)}/topics/${encodeURIComponent(
      topicId
    )}/students${buildQuery({ scope: nonDefault(scope, 'all') })}`
  );

  return unwrap(response);
};

export const getCohortPerformanceStudents = async ({
  cohortId,
  scope,
  window,
}: {
  cohortId: string;
  scope?: CohortPerformanceScope;
  window?: CohortPerformanceWindow;
}): Promise<CohortPerformanceStudents> => {
  const response = await axiosInstance.get<
    ApiResponse<CohortPerformanceStudents>
  >(
    `${cohortPerformanceBase(cohortId)}/students${buildQuery({
      scope: nonDefault(scope, 'all'),
      window: nonDefault(window, 'all'),
    })}`
  );

  return unwrap(response);
};

export const getCohortPerformanceSelfAssessments = async (
  cohortId: string
): Promise<CohortPerformanceSelfAssessments> => {
  const response = await axiosInstance.get<
    ApiResponse<CohortPerformanceSelfAssessments>
  >(`${cohortPerformanceBase(cohortId)}/self-assessments`);

  return unwrap(response);
};
